import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { supportApi } from '@/api/support';

/**
 * Shared support-chat state (manager/support). Opened from several places (FAB, manager-chip, "Top up",
 * the "Transfers" tab). A module-level ref is the single source of truth.
 *
 * <p>On narrow screens the chat opens as its own `/support` page (route exists in the vitrine bundle)
 * rather than a drawer overlay (which eats space on mobile). On wide (desktop-frame) screens it's the
 * drawer via {@link supportOpen}.
 */
const supportOpen = ref(false);
const supportDraft = ref('');
/** When set, the chat surface binds to THIS specific thread (e.g. a top-up request's own chat) instead of
 *  the general manager chat. Null = general. */
const supportTicketId = ref<string | null>(null);

/**
 * Unread across all threads — the piece the client was missing to learn about a message WITHOUT opening the
 * chat (B1).
 *
 * Module-level state, like everything else here: several surfaces render the badge (the manager chip on the
 * card screen, the global FAB), and each keeping its own count would inevitably drift from its neighbour.
 */
const unreadTotal = ref(0);
const unreadByTicket = ref<Record<string, number>>({});
/** Unread across EVERY thread, concierge tasks included — the dot on the cabinet's «Консьерж» item, which is the one
 *  window for all of them (docs/cabinet-redesign/02_ЭКРАНЫ_БЫЛО_СТАЛО.md §3.5). The general chat keeps unreadTotal. */
const unreadAll = computed(() => Object.values(unreadByTicket.value).reduce((sum, n) => sum + n, 0));

/**
 * Which threads are concierge tasks. They live on the task's screen, so the general chat's badge leaves them out
 * (CONCIERGE_CHAT_FIRST.md §5.2 «Непрочитанное», CF-021). `/unread` does not name a thread's kind: the thread list is
 * asked only when a thread this tab has not seen yet shows up in the counter.
 */
const threadKinds = new Map<string, string>();
const conciergeTicketIds = ref<Set<string>>(new Set());

async function learnThreadKinds(ids: string[]) {
  if (ids.every(id => threadKinds.has(id))) return;
  const owned = await supportApi.myTickets();
  for (const th of owned) threadKinds.set(th.id, th.refType ?? 'GENERAL');
  // A thread the list does not return is taken as general, and not asked about again.
  for (const id of ids) if (!threadKinds.has(id)) threadKinds.set(id, 'GENERAL');
  conciergeTicketIds.value = new Set([...threadKinds].filter(([, k]) => k === 'CONCIERGE').map(([id]) => id));
}

/** The general chat's badge: every thread but the concierge tasks'. */
function recountUnread() {
  unreadTotal.value = Object.entries(unreadByTicket.value)
    .filter(([id]) => !conciergeTicketIds.value.has(id))
    .reduce((sum, [, n]) => sum + n, 0);
}

/** Polling runs only while the tab is visible — a background tab must not hit the server every 8 seconds. */
const UNREAD_POLL_MS = 8_000;
let unreadTimer: ReturnType<typeof setInterval> | null = null;

async function refreshUnread() {
  try {
    const u = await supportApi.myUnread();
    unreadByTicket.value = Object.fromEntries(u.byTicket.map(t => [t.ticketId, t.unread]));
    await learnThreadKinds(u.byTicket.map(t => t.ticketId)).catch(() => { /* unknown kinds count as general */ });
    recountUnread();
  } catch {
    // The counter is decoration: if the network blinked, keep the previous number and wait for the next poll.
    // Never zero it on error — a vanished badge reads as "no messages", which would be a lie.
  }
}

const NARROW_QUERY = '(max-width: 600px)';

export function useSupport() {
  const router = useRouter();

  /**
   * Open the chat surface: a full-page `/support` route on narrow screens (when that route exists),
   * otherwise the drawer.
   */
  function openSurface() {
    // The client cabinet: the concierge tab is the one window for every chat — always a route, never a drawer.
    if (router.hasRoute('concierge')) {
      const thread = supportTicketId.value ?? 'general';
      router.push({ name: 'concierge', query: { thread } }).catch(() => { /* duplicate nav — ignore */ });
      return;
    }
    const narrow = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(NARROW_QUERY).matches;
    if (narrow && router.hasRoute('support')) {
      router.push({ name: 'support' }).catch(() => { /* duplicate nav — ignore */ });
    } else {
      supportOpen.value = true;
    }
  }

  /** Open the GENERAL manager chat. {@code prefill} fills the composer only if it's empty. */
  function openSupport(prefill?: string) {
    supportTicketId.value = null;
    if (prefill && !supportDraft.value.trim()) {
      supportDraft.value = prefill;
    }
    openSurface();
  }

  /** Open a SPECIFIC thread (e.g. a top-up request's chat) so messages stay scoped to it. */
  function openThread(ticketId: string) {
    supportTicketId.value = ticketId;
    openSurface();
  }

  function closeSupport() { supportOpen.value = false; }

  /**
   * Start polling for unread. Idempotent — safe to call from several places, there will be one timer.
   * Stopping on sign-out is mandatory: otherwise polling keeps knocking after logout, without a token.
   */
  function startUnreadPolling() {
    if (unreadTimer) return;
    refreshUnread();
    unreadTimer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') refreshUnread();
    }, UNREAD_POLL_MS);
  }

  function stopUnreadPolling() {
    if (unreadTimer) clearInterval(unreadTimer);
    unreadTimer = null;
    unreadTotal.value = 0;
    unreadByTicket.value = {};
    threadKinds.clear();
    conciergeTicketIds.value = new Set();
  }

  return {
    supportOpen, supportDraft, supportTicketId, openSupport, openThread, closeSupport,
    unreadTotal, unreadAll, unreadByTicket, refreshUnread, recountUnread, startUnreadPolling, stopUnreadPolling,
  };
}
